import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, savedTeamsApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Select, TextInput } from '../ui.tsx';
import { CheckboxPicker } from '../checkbox-picker.tsx';

/** Ordered, controlled IDs preserve the lead and the caller's schedule draft. */
export function AssigneePicker({
  value,
  onChange,
  error,
  disabled,
  label,
  compact = false,
  teamId,
  onTeamChange,
}: {
  teamId?: string | null;
  onTeamChange?: (id: string | null, members: string[]) => void;
  value: string[];
  onChange: (next: string[]) => void;
  error?: string;
  disabled?: boolean;
  label?: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const { currentLocation } = useSession();
  const [teamSearch, setTeamSearch] = useState('');
  const saved = useQuery({
    queryKey: ['saved-teams'],
    queryFn: () => savedTeamsApi.list(),
    enabled: Boolean(onTeamChange),
  });
  const roster = useQuery({ queryKey: ['team'], queryFn: () => companyApi.users() });
  const people = (roster.data?.items ?? []).filter(
    (member) =>
      value.includes(member.id) ||
      (member.isActive && (!currentLocation || member.locationIds?.includes(currentLocation.id))),
  );
  const teams = (saved.data?.items ?? []).filter(
    (entry) =>
      entry.id === teamId ||
      (entry.isActive && entry.name.toLocaleLowerCase().includes(teamSearch.toLocaleLowerCase())),
  );
  function changeIndividuals(next: string[]) {
    if (teamId && onTeamChange) onTeamChange(null, next);
    else onChange(next);
  }
  return (
    <div className="mb-3 min-w-0">
      {onTeamChange ? (
        <div className="mb-4 rounded-md border border-[color:var(--color-line)] p-3">
          <TextInput
            label={t('employeePicker.searchTeams')}
            type="search"
            value={teamSearch}
            disabled={disabled}
            onChange={(event) => setTeamSearch(event.target.value)}
          />
          <Select
            label={t('employeePicker.savedTeam')}
            value={teamId ?? ''}
            disabled={disabled || saved.isPending}
            options={[
              { value: '', label: t('employeePicker.individuals') },
              ...teams.map((entry) => ({
                value: entry.id,
                label: t('savedTeams.chip', { name: entry.name, count: entry.memberIds.length }),
              })),
            ]}
            onChange={(event) => {
              const selected = teams.find((entry) => entry.id === event.target.value);
              if (!event.target.value) onTeamChange(null, value);
              else if (selected?.isActive) onTeamChange(selected.id, selected.memberIds);
            }}
          />
          {saved.isError ? <Alert tone="error">{t('savedTeams.loadError')}</Alert> : null}
          {saved.isSuccess && saved.data.items.every((entry) => !entry.isActive) ? (
            <p className="field-hint">{t('employeePicker.noSavedTeams')}</p>
          ) : null}
        </div>
      ) : null}
      {roster.isError ? <Alert tone="error">{t('employeePicker.loadError')}</Alert> : null}
      {roster.isPending ? <p role="status">{t('employeePicker.loading')}</p> : null}
      <CheckboxPicker
        label={label ?? t('assigneePicker.label')}
        value={value}
        onChange={changeIndividuals}
        disabled={disabled}
        maxSelected={20}
        lead
        choices={people.map((member) => ({
          id: member.id,
          name: member.fullName,
          detail: member.isActive ? member.email : t('employeePicker.inactive'),
          disabled: !member.isActive,
        }))}
      />
      {roster.isSuccess && people.length === 0 ? <p className="field-hint">{t('assigneePicker.noTeam')}</p> : null}
      {!compact ? (
        <p className="field-hint">
          {value.length === 0
            ? t('assigneePicker.nobodyHint')
            : value.length === 1
              ? t('assigneePicker.oneHint')
              : t('assigneePicker.crewHint', { size: value.length })}
        </p>
      ) : null}
      {error ? (
        <p className="field-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
